import React from 'react';
import { motion } from 'framer-motion';
import { CreditCard, Sparkles, Clock, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/Button';
const benefits = [{
  icon: Sparkles,
  title: 'Premium Finishes',
  description: 'Matte, gloss, soft-touch, spot UV, foil stamping, and more'
}, {
  icon: CreditCard,
  title: 'Quality Card Stocks',
  description: 'From standard 14pt to ultra-thick 32pt and specialty papers'
}, {
  icon: Clock,
  title: 'Fast Turnaround',
  description: 'Standard orders ship in 3-5 business days, rush available'
}];
const faqs = [{
  question: 'What paper stocks do you offer?',
  answer: 'We offer a range of card stocks including 14pt, 16pt, 18pt, and 32pt. We also have specialty options like cotton, linen, kraft, and recycled papers. Contact us for samples.'
}, {
  question: 'What finishing options are available?',
  answer: 'We offer matte finish, gloss finish, soft-touch lamination, spot UV coating, foil stamping (gold, silver, rose gold), embossing, debossing, and rounded corners.'
}, {
  question: 'What is the standard business card size?',
  answer: 'The standard US business card size is 3.5" x 2". We also offer square cards (2.5" x 2.5"), mini cards, folded cards, and custom die-cut shapes.'
}, {
  question: 'What are your minimum order quantities?',
  answer: 'Our minimum order is 100 cards. We offer quantity breaks at 250, 500, 1000, and 2500+ cards. Larger quantities mean better per-card pricing.'
}, {
  question: 'Can you print double-sided?',
  answer: 'Yes, we print single-sided and double-sided cards. Double-sided printing is a great way to include more information or add visual impact to your cards.'
}, {
  question: 'Do you offer design services?',
  answer: "Yes. We can help design your business cards from scratch or refine your existing design. We'll ensure your artwork is print-ready and optimized for the best results."
}];
export function BusinessCards() {
  return <div className="min-h-screen bg-atomic-black" data-id="element-554">
      {/* Hero Section */}
      <section className="relative py-20 md:py-32 overflow-hidden" data-id="element-555">
        <div className="absolute inset-0 z-0" data-id="element-556">
          <img src="https://cdn.magicpatterns.com/uploads/5MWBWmK1GS3CAjG4Akfnqo/Premium_business_cards_with_professional_finishes.jpg" alt="Premium business cards with professional finishes" className="absolute inset-0 w-full h-full object-cover" data-id="element-557" />
          <div className="absolute inset-0 bg-atomic-black/80" data-id="element-558" />
          <div className="absolute inset-0 bg-gradient-to-b from-atomic-black/60 via-atomic-black/70 to-atomic-black/90" data-id="element-559" />
        </div>
        <div className="container mx-auto px-4 md:px-6 relative z-10" data-id="element-560">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} className="max-w-3xl mx-auto text-center" data-id="element-561">
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight" data-id="element-562">
              Business Cards{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-atomic-green to-lime-400" data-id="element-563">
                That Leave an Impression
              </span>
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed mb-8" data-id="element-564">
              Premium business cards with professional finishes. From classic
              designs to luxury options with foil, embossing, and specialty
              papers.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-565">
              <a href="/contact" data-id="element-566">
                <Button size="lg" data-id="element-567">
                  Request Card Quote
                  <ArrowRight className="ml-2 w-4 h-4" data-id="element-568" />
                </Button>
              </a>
              <a href="tel:517-414-9232" data-id="element-569">
                <Button variant="outline" size="lg" data-id="element-570">
                  Call 517-414-9232
                </Button>
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 bg-atomic-black border-t border-white/5" data-id="element-571">
        <div className="container mx-auto px-4 md:px-6" data-id="element-572">
          <div className="max-w-5xl mx-auto" data-id="element-573">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} className="text-center mb-12" data-id="element-574">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" data-id="element-575">
                Why Choose Our Business Cards
              </h2>
              <p className="text-lg text-gray-300" data-id="element-576">
                Quality printing that represents your brand
              </p>
            </motion.div>
            <div className="grid md:grid-cols-3 gap-8" data-id="element-577">
              {benefits.map((benefit, index) => <motion.div key={benefit.title} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: index * 0.1
            }} className="bg-atomic-charcoal rounded-xl p-6 border border-white/10" data-id="element-578">
                  <div className="w-12 h-12 rounded-lg bg-atomic-green/10 flex items-center justify-center text-atomic-green mb-4 border border-atomic-green/20" data-id="element-579">
                    <benefit.icon size={24} data-id="element-580" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-3" data-id="element-581">
                    {benefit.title}
                  </h3>
                  <p className="text-gray-400 leading-relaxed" data-id="element-582">
                    {benefit.description}
                  </p>
                </motion.div>)}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-atomic-charcoal border-t border-white/5" data-id="element-583">
        <div className="container mx-auto px-4 md:px-6" data-id="element-584">
          <div className="max-w-3xl mx-auto" data-id="element-585">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} className="text-center mb-12" data-id="element-586">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" data-id="element-587">
                Frequently Asked Questions
              </h2>
              <p className="text-lg text-gray-300" data-id="element-588">
                Common questions about business cards
              </p>
            </motion.div>
            <div className="space-y-6" data-id="element-589">
              {faqs.map((faq, index) => <motion.div key={index} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: index * 0.05
            }} className="bg-atomic-black rounded-xl p-6 border border-white/10" data-id="element-590">
                  <h3 className="text-lg font-bold text-white mb-3" data-id="element-591">
                    {faq.question}
                  </h3>
                  <p className="text-gray-400 leading-relaxed" data-id="element-592">{faq.answer}</p>
                </motion.div>)}
            </div>
          </div>
        </div>
      </section>
    </div>;
}